/**
 * 用户信息管理 composable
 */
import { computed } from "vue";
import { UserRole } from "~/enums";

export interface CurrentAuthUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
}

interface AuthPayload {
  email: string;
  password: string;
  name?: string;
  anonymousUserId?: string;
}

interface UpdateProfilePayload {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

let refreshUserPromise: Promise<CurrentAuthUser | null> | null = null;

/**
 * 获取当前用户信息
 */
export function useAuthUser() {
  const user = useState<CurrentAuthUser | null>("auth-user", () => null); // 使用 useState 管理状态，支持 SSR 和 CSR 保持一致
  const initialized = useState("auth-user-initialized", () => false); // 用于标记用户信息是否已初始化
  const isLoading = useState("auth-user-loading", () => false); // 用于标记用户信息加载状态

  const isLoggedIn = computed(() => !!user.value); // 判断用户是否已登录
  const isAdmin = computed(() => user.value?.role === UserRole.ADMIN); // 判断用户是否为管理员

  /**
   * 刷新用户信息
   * @returns
   */
  const refreshUser = async () => {
    if (refreshUserPromise) return refreshUserPromise;

    // 如果用户信息已初始化，则直接返回
    refreshUserPromise = (async () => {
      isLoading.value = true;
      try {
        const response: any = await $fetch("/api/auth/me");
        user.value = response.user || null;
        initialized.value = true;
        return user.value;
      } catch (error) {
        user.value = null;
        initialized.value = true;
        return null;
      } finally {
        isLoading.value = false;
        refreshUserPromise = null;
      }
    })();

    return refreshUserPromise;
  };

  /**
   * 登录并获取用户信息
   */
  const login = async (payload: AuthPayload) => {
    const response: any = await $fetch("/api/auth/login", {
      method: "POST",
      body: payload,
    });
    user.value = response.user || null;
    initialized.value = true;
    return user.value;
  };

  /**
   * 注册并获取用户信息
   */
  const register = async (payload: AuthPayload) => {
    const response: any = await $fetch("/api/auth/register", {
      method: "POST",
      body: payload,
    });
    user.value = response.user || null;
    initialized.value = true;
    return user.value;
  };

  /**
   * 更新当前用户资料
   */
  const updateProfile = async (payload: UpdateProfilePayload) => {
    const response: any = await $fetch("/api/auth/profile", {
      method: "PATCH",
      body: payload,
    });
    user.value = response.user || null;
    initialized.value = true;
    return user.value;
  };

  /**
   * 登出并清除用户信息
   */
  const logout = async () => {
    await $fetch("/api/auth/logout", { method: "POST" });
    user.value = null;
    initialized.value = true;
  };

  return {
    user,
    initialized,
    isLoading,
    isLoggedIn,
    isAdmin,
    refreshUser,
    login,
    register,
    updateProfile,
    logout,
  };
}
